import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Camera, Zap, Clock, CheckCircle2 } from 'lucide-react';

const faqs = [
  {
    q: '¿Qué es el OCR de facturas para restaurantes?',
    a: 'El OCR (Reconocimiento Óptico de Caracteres) es una tecnología de inteligencia artificial que extrae automáticamente los datos de tus facturas y albaranes: proveedor, ingredientes, cantidades, precios y totales. Basta con hacer una foto con el móvil.',
  },
  {
    q: '¿Cómo funciona el escáner OCR de PLEKO?',
    a: 'Haces una foto a la factura o albarán con tu móvil. La IA de PLEKO (basada en GPT-4 Vision) analiza la imagen, extrae cada línea de producto con su precio y cantidad, y te propone actualizar los precios de tus ingredientes automáticamente. Tú solo confirmas.',
  },
  {
    q: '¿Cuántas facturas puedo escanear al mes?',
    a: 'Depende del plan: BASIC incluye 15 escaneos/mes, PRO 60 escaneos/mes, y SCALE 200 escaneos/mes. Cada escaneo puede contener una factura o albarán completo con múltiples líneas.',
  },
  {
    q: '¿Funciona con albaranes manuscritos?',
    a: 'La IA funciona mejor con facturas impresas o digitales, pero puede procesar albaranes con letra clara. La precisión es del 95%+ en documentos impresos y del 80-90% en manuscritos legibles. Siempre puedes revisar y corregir antes de confirmar.',
  },
  {
    q: '¿Los precios se actualizan solos en las recetas?',
    a: 'Sí. Cuando confirmas los precios extraídos del OCR, PLEKO actualiza el precio del ingrediente y recalcula automáticamente el food cost de todas las recetas que lo contienen. Si algún plato supera tu umbral de food cost, recibes una alerta.',
  },
];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

export default function OcrFacturasPage() {
  return (
    <>
      <Helmet>
        <title>OCR Facturas Restaurante: Digitaliza Albaranes con IA [2026] | PLEKO</title>
        <meta
          name="description"
          content="Escanea facturas y albaranes de tu restaurante con IA. Actualiza precios de ingredientes automáticamente y mantén tus escandallos al día. OCR con GPT-4 Vision."
        />
        <meta name="keywords" content="OCR facturas restaurante, digitalizar albaranes hostelería, escanear facturas restaurante, IA facturas hostelería, actualizar precios ingredientes automático, OCR hostelería" />
        <link rel="canonical" href="https://pleko.app/ocr-facturas-restaurante" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/ocr-facturas-restaurante" />
        <meta property="og:title" content="OCR Facturas Restaurante: Digitaliza Albaranes con IA [2026] | PLEKO" />
        <meta property="og:description" content="Escanea facturas y albaranes de tu restaurante con IA. Actualiza precios de ingredientes automáticamente y mantén tus escandallos al día. OCR con GPT-4 Vision." />
        <meta property="og:image" content="https://pleko.app/og-image.png" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OCR Facturas Restaurante: Digitaliza Albaranes con IA [2026] | PLEKO" />
        <meta name="twitter:description" content="Escanea facturas y albaranes de tu restaurante con IA. Actualiza precios de ingredientes automáticamente y mantén tus escandallos al día. OCR con GPT-4 Vision." />
        <meta name="twitter:image" content="https://pleko.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-b from-cyan-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm font-medium mb-6">
              <Camera className="w-4 h-4" />
              OCR con inteligencia artificial
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              Digitaliza tus facturas<br />
              <span className="text-cyan-500">con una foto desde el móvil</span>
            </h1>
            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
              Haz una foto al albarán del proveedor y PLEKO extrae ingredientes, precios y cantidades
              con IA. Tus escandallos se actualizan solos.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-500 text-white font-semibold rounded-xl hover:bg-cyan-600 transition-colors"
            >
              Probar el OCR gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Cómo funciona */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
            Cómo funciona en 3 pasos
          </h2>
          <p className="text-slate-600 text-center mb-10">
            De la factura en papel a los costes actualizados en menos de 30 segundos
          </p>

          <div className="space-y-4 mb-16">
            {[
              {
                step: '1',
                icon: Camera,
                title: 'Haz una foto a la factura o albarán',
                desc: 'Desde la app de PLEKO, pulsa el botón de OCR y fotografía el documento. Funciona con facturas impresas, PDF, e incluso albaranes a mano.',
              },
              {
                step: '2',
                icon: Zap,
                title: 'La IA extrae los datos automáticamente',
                desc: 'GPT-4 Vision analiza la imagen y extrae cada línea: nombre del producto, cantidad, unidad, precio unitario y total. Precisión del 95%+ en documentos impresos.',
              },
              {
                step: '3',
                icon: CheckCircle2,
                title: 'Confirma y actualiza los precios',
                desc: 'Revisa los datos extraídos, corrige si es necesario, y PLEKO actualiza los precios de tus ingredientes. Todos los escandallos se recalculan al instante.',
              },
            ].map(({ step, icon: Icon, title, desc }) => (
              <div key={step} className="bg-slate-50 rounded-xl p-6 flex gap-4 border border-slate-200">
                <div className="w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-lg">
                  {step}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
                    <Icon className="w-4 h-4 text-cyan-500" />
                    {title}
                  </h3>
                  <p className="text-slate-600 text-sm">{desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">¿Por qué digitalizar tus facturas?</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La mayoría de restaurantes reciben entre <strong>5 y 15 albaranes por semana</strong>. Cada albarán contiene entre 10 y 30 líneas de producto con precios que pueden variar respecto a la última compra.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              Actualizar manualmente estos precios en una hoja de Excel es tedioso y propenso a errores. Peor aún: la mayoría de restaurantes <strong>no lo hacen</strong>, y sus escandallos acaban desactualizados en semanas.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">El problema de los precios desactualizados</h2>
            <div className="grid md:grid-cols-3 gap-4 mb-10">
              {[
                {
                  icon: Clock,
                  title: '2-4 horas/semana',
                  desc: 'Tiempo medio que un restaurante dedica a actualizar precios manualmente (si lo hace).',
                },
                {
                  icon: Zap,
                  title: '5-10% error en food cost',
                  desc: 'Diferencia típica entre el food cost teórico con precios antiguos y el food cost real.',
                },
                {
                  icon: Camera,
                  title: '30 segundos con OCR',
                  desc: 'Tiempo para escanear un albarán completo y actualizar todos los precios automáticamente.',
                },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="bg-cyan-50 border border-cyan-200 rounded-xl p-5 text-center">
                  <Icon className="w-8 h-8 text-cyan-500 mx-auto mb-3" />
                  <p className="font-bold text-slate-900 mb-1">{title}</p>
                  <p className="text-slate-600 text-sm">{desc}</p>
                </div>
              ))}
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">Qué documentos puede escanear PLEKO</h2>
            <div className="grid md:grid-cols-2 gap-3 mb-10">
              {[
                'Facturas de proveedores (impresas o PDF)',
                'Albaranes de entrega',
                'Tickets de compra en supermercados mayoristas',
                'Pedidos a Cash & Carry (Makro, GM Cash, etc.)',
                'Facturas digitales por email (capturas de pantalla)',
                'Notas de entrega con precios manuscritos',
              ].map((item) => (
                <div key={item} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <CheckCircle2 className="w-4 h-4 text-cyan-500 flex-shrink-0" />
                  <span className="text-sm text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">OCR + actualización de precios = food cost siempre al día</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El verdadero poder del OCR de PLEKO no es solo digitalizar la factura: es que al confirmar los precios, <strong>todos tus escandallos se recalculan al instante</strong>. Si un ingrediente ha subido un 15%, inmediatamente sabrás qué platos tienen ahora un food cost demasiado alto.
            </p>
            <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-6 mb-10">
              <p className="text-cyan-800 font-medium text-center text-lg">
                Ejemplo: escaneas un albarán donde las gambas han subido de 18€/kg a 22€/kg.
                PLEKO recalcula las 8 recetas que usan gambas y te alerta que la paella de mariscos
                ha pasado del 30% al 37% de food cost.
              </p>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Preguntas frecuentes sobre el OCR</h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-2xl p-8 text-center text-white">
            <Camera className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">Deja de teclear facturas a mano</h2>
            <p className="text-cyan-100 mb-6 max-w-xl mx-auto">
              Foto, confirmar, listo. PLEKO actualiza todos los precios y recalcula tus escandallos en segundos.
              14 días gratis, sin tarjeta de crédito.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-cyan-600 font-bold rounded-xl hover:bg-cyan-50 transition-colors"
            >
              Probar el OCR gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
